import React, { useId, useRef, useState } from "react";
import type { CreateTrialInvitePayload } from "../../../services/trialInviteApi";
import {
  EXPIRATION_NOTICE_DAYS,
  TRIAL_DAYS_DEFAULT,
  TRIAL_DAYS_MAX,
  TRIAL_DAYS_MIN,
  URGENT_EXPIRATION_NOTICE_DAYS,
  VALID_DAYS_DEFAULT,
  VALID_DAYS_MAX,
  VALID_DAYS_MIN,
} from "./constants";

export type NewInviteValues = Omit<CreateTrialInvitePayload, "brandId">;

interface NewInviteFormProps {
  disabled: boolean;
  isPending: boolean;
  error: string | null;
  /** Resolves to true when the invite was created (the e-mail field is then cleared). */
  onSubmit: (values: NewInviteValues) => Promise<boolean>;
}

type DaysField = "trialDays" | "validDays";

const RANGES: Record<DaysField, { min: number; max: number }> = {
  trialDays: { min: TRIAL_DAYS_MIN, max: TRIAL_DAYS_MAX },
  validDays: { min: VALID_DAYS_MIN, max: VALID_DAYS_MAX },
};

/** A whole number of days within the field's range, else null. */
const parseDays = (field: DaysField, value: string): number | null => {
  if (!/^\d+$/.test(value.trim())) return null;
  const days = Number(value);
  const { min, max } = RANGES[field];
  return days >= min && days <= max ? days : null;
};

const rangeError = (field: DaysField) =>
  `Podaj liczbę dni od ${RANGES[field].min} do ${RANGES[field].max}.`;

/**
 * The "okres próbny kończy się" mails go out 7 and 2 days before the end, so
 * a short trial gets them right after registration.
 */
const shortTrialHint = (trialDays: number | null): string => {
  if (trialDays === null || trialDays > EXPIRATION_NOTICE_DAYS) return "";
  return trialDays <= URGENT_EXPIRATION_NOTICE_DAYS
    ? `Przy okresie próbnym do ${URGENT_EXPIRATION_NOTICE_DAYS} dni oba maile „Twój okres próbny kończy się…” (${EXPIRATION_NOTICE_DAYS} i ${URGENT_EXPIRATION_NOTICE_DAYS} dni przed końcem) wyjdą w ciągu doby od rejestracji.`
    : `Przy okresie próbnym do ${EXPIRATION_NOTICE_DAYS} dni mail „Twój okres próbny kończy się…” (${EXPIRATION_NOTICE_DAYS} dni przed końcem) wyjdzie zaraz po rejestracji.`;
};

const INPUT_CLASS = "mt-1 w-full border border-neutral-300 rounded-md px-3 py-2";

interface DaysInputProps {
  field: DaysField;
  label: string;
  value: string;
  error: string | undefined;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (value: string) => void;
  /** Extra description below the range (rendered by the caller). */
  hintId?: string;
}

const DaysInput: React.FC<DaysInputProps> = ({
  field,
  label,
  value,
  error,
  inputRef,
  onChange,
  hintId,
}) => {
  const id = useId();
  const { min, max } = RANGES[field];
  const describedBy = [`${id}-range`, error && `${id}-error`, hintId]
    .filter(Boolean)
    .join(" ");
  return (
    <div className="block text-sm">
      <label htmlFor={id} className="font-medium text-neutral-700">
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={INPUT_CLASS}
      />
      <p id={`${id}-range`} className="text-xs text-neutral-500">
        Od {min} do {max} dni.
      </p>
      {error && (
        <p id={`${id}-error`} className="text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
};

const NewInviteForm: React.FC<NewInviteFormProps> = ({
  disabled,
  isPending,
  error,
  onSubmit,
}) => {
  const emailId = useId();
  const shortTrialHintId = useId();
  const [email, setEmail] = useState("");
  // Strings, not numbers: Number("") is 0, which would block clearing a field.
  const [days, setDays] = useState<Record<DaysField, string>>({
    trialDays: String(TRIAL_DAYS_DEFAULT),
    validDays: String(VALID_DAYS_DEFAULT),
  });
  const [errors, setErrors] = useState<Partial<Record<DaysField, string>>>({});
  const inputRefs = {
    trialDays: useRef<HTMLInputElement>(null),
    validDays: useRef<HTMLInputElement>(null),
  };
  const hint = shortTrialHint(parseDays("trialDays", days.trialDays));

  const changeDays = (field: DaysField, value: string) => {
    setDays((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trialDays = parseDays("trialDays", days.trialDays);
    const validDays = parseDays("validDays", days.validDays);
    if (trialDays === null || validDays === null) {
      setErrors({
        trialDays: trialDays === null ? rangeError("trialDays") : undefined,
        validDays: validDays === null ? rangeError("validDays") : undefined,
      });
      inputRefs[trialDays === null ? "trialDays" : "validDays"].current?.focus();
      return;
    }
    const created = await onSubmit({
      email: email.trim() || undefined,
      trialDays,
      validDays,
    });
    if (created) setEmail("");
  };

  return (
    <form
      onSubmit={handleSubmit}
      // Range errors are ours (Polish, inline); the e-mail is checked by the API.
      noValidate
      className="bg-white rounded-lg border border-neutral-200 p-6 space-y-4"
      aria-label="Nowe zaproszenie"
    >
      <div className="grid md:grid-cols-3 gap-4">
        <div className="block text-sm">
          <label htmlFor={emailId} className="font-medium text-neutral-700">
            E-mail klienta (opcjonalnie)
          </label>
          <input
            id={emailId}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="kontakt@firma.pl"
            aria-describedby={`${emailId}-help`}
            className={INPUT_CLASS}
          />
          <p id={`${emailId}-help`} className="text-xs text-neutral-500">
            Jeśli podasz, rejestracja przejdzie tylko na ten adres.
          </p>
        </div>
        <div>
          <DaysInput
            field="trialDays"
            label="Długość okresu próbnego (dni)"
            value={days.trialDays}
            error={errors.trialDays}
            inputRef={inputRefs.trialDays}
            onChange={(value) => changeDays("trialDays", value)}
            hintId={hint ? shortTrialHintId : undefined}
          />
          {/* Always mounted so the hint is announced when it appears. */}
          <p
            id={shortTrialHintId}
            aria-live="polite"
            className="mt-1 text-xs text-amber-800"
          >
            {hint}
          </p>
        </div>
        <DaysInput
          field="validDays"
          label="Ważność linku (dni)"
          value={days.validDays}
          error={errors.validDays}
          inputRef={inputRefs.validDays}
          onChange={(value) => changeDays("validDays", value)}
        />
      </div>
      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={disabled || isPending}
        className="px-5 py-2 bg-primary-700 text-white text-sm font-medium rounded-lg hover:bg-primary-800 disabled:opacity-50"
      >
        {isPending ? "Tworzę link..." : "Wygeneruj link"}
      </button>
    </form>
  );
};

export default NewInviteForm;
